import React, { useEffect, useMemo, useState } from "react";
import { ChevronRight, Loader2, ShoppingBag, Trash2 } from "lucide-react";
import { fetchMenu } from "@/lib/api";

const QtyInput = ({ value, onInc, onDec, onSet, testId, autoFocus }) => (
  <div className="inline-flex items-center border-2 border-dotr-ink bg-white shrink-0">
    <button
      type="button"
      onClick={onDec}
      className="px-2.5 h-9 font-heading font-bold border-r-2 border-dotr-ink hover:bg-dotr-muted text-base"
      data-testid={`${testId}-dec`}
      aria-label="Decrease quantity"
    >
      −
    </button>
    <input
      type="number"
      min={1}
      max={999}
      autoFocus={autoFocus}
      value={value || ""}
      onChange={(e) => {
        const v = parseInt(e.target.value, 10);
        onSet(Number.isFinite(v) && v > 0 ? Math.min(999, v) : 1);
      }}
      className="w-12 h-9 text-center font-mono font-bold text-sm outline-none"
      data-testid={testId}
    />
    <button
      type="button"
      onClick={onInc}
      className="px-2.5 h-9 font-heading font-bold border-l-2 border-dotr-ink hover:bg-dotr-muted text-base"
      data-testid={`${testId}-inc`}
      aria-label="Increase quantity"
    >
      +
    </button>
  </div>
);

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const fmtMoney = (sym, n) => `${sym}${(Number(n) || 0).toFixed(2)}`;

export const MenuStep = ({ value, onChange, totalQty, onPricesLoaded }) => {
  const [menu, setMenu] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState("£");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadMenu = () => {
    setLoading(true);
    setError(null);
    fetchMenu()
      .then((d) => {
        setMenu(d?.menu || []);
        if (d?.currency_symbol) setCurrencySymbol(d.currency_symbol);
        if (!d?.menu || d.menu.length === 0) {
          setError("Menu came back empty. Try refreshing.");
        }
      })
      .catch((e) => {
        console.error("[menu] fetch failed", e);
        setError(e?.message || "Could not load menu.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const priceMap = useMemo(() => {
    const m = {};
    menu.forEach((cat) =>
      cat.items.forEach((it) => {
        m[it.name] = Number(it.price) || 0;
      })
    );
    return m;
  }, [menu]);

  useEffect(() => {
    if (onPricesLoaded && Object.keys(priceMap).length > 0) {
      onPricesLoaded(priceMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceMap]);

  const itemsMap = value || {}; // { itemName: quantity }

  const grandTotal = useMemo(() => {
    return Object.entries(itemsMap).reduce(
      (sum, [name, qty]) => sum + (priceMap[name] || 0) * (parseInt(qty, 10) || 0),
      0
    );
  }, [itemsMap, priceMap]);

  const toggle = (name) => {
    onChange((prev) => {
      const cur = prev || {};
      const next = { ...cur };
      if (next[name]) delete next[name];
      else next[name] = 1;
      return next;
    });
  };

  const setQty = (name, qty) => {
    onChange((prev) => ({ ...(prev || {}), [name]: qty }));
  };

  const incQty = (name) => {
    onChange((prev) => {
      const cur = (prev && prev[name]) || 0;
      return { ...(prev || {}), [name]: Math.min(999, cur + 1) };
    });
  };

  const decQty = (name) => {
    onChange((prev) => {
      const cur = (prev && prev[name]) || 1;
      return { ...(prev || {}), [name]: Math.max(1, cur - 1) };
    });
  };

  const remove = (name) => {
    onChange((prev) => {
      const next = { ...(prev || {}) };
      delete next[name];
      return next;
    });
  };

  if (loading) {
    return (
      <div className="brutal-card p-10 text-center" data-testid="menu-loading">
        <Loader2 className="animate-spin inline-block" />
        <div className="font-mono text-xs mt-2 uppercase tracking-widest text-dotr-inkmuted">Loading menu…</div>
      </div>
    );
  }

  if (error || menu.length === 0) {
    return (
      <div className="brutal-card p-8 text-center" data-testid="menu-error">
        <div className="font-heading font-semibold text-2xl uppercase mb-2">Menu could not load</div>
        <p className="font-mono text-xs text-dotr-inkmuted mb-4">
          {error || "The menu list is empty."} Try a hard refresh (⌘/Ctrl + Shift + R).
        </p>
        <button type="button" onClick={loadMenu} className="brutal-btn px-5 py-2 text-xs" data-testid="btn-retry-menu">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="menu-step">
      <div className="lg:col-span-8 space-y-6">
        {menu.map((cat) => (
          <div key={cat.category} className="brutal-card p-5 md:p-6" data-testid={`menu-cat-${slugify(cat.category)}`}>
            <div className="flex items-baseline justify-between mb-4 border-b-2 border-dotr-ink pb-3">
              <h3 className="font-heading italic font-medium text-xl tracking-tight">
                {cat.category}
              </h3>
              <span className="font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted">
                {cat.items.length} items
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {cat.items.map((it) => {
                const name = it.name;
                const price = it.price;
                const upcoming = it.upcoming_price;
                const upcomingFrom = it.upcoming_from;
                const checked = !!itemsMap[name];
                const slug = slugify(name);
                const lineTotal = (priceMap[name] || 0) * (itemsMap[name] || 0);
                return (
                  <div
                    key={name}
                    className={`relative z-10 border-2 border-dotr-ink p-3 transition-all ${
                      checked ? "bg-dotr-bg shadow-brutalSm" : "bg-white"
                    }`}
                    data-testid={`menu-item-${slug}`}
                  >
                    <label className="flex items-center gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(name)}
                        className="w-5 h-5 accent-dotr-primary cursor-pointer"
                        data-testid={`menu-check-${slug}`}
                      />
                      <span className="font-body text-base flex-1">{name}</span>
                      <span className="font-mono text-xs font-bold text-dotr-ink whitespace-nowrap" data-testid={`menu-price-${slug}`}>
                        {fmtMoney(currencySymbol, price)}
                      </span>
                    </label>
                    {upcoming != null && upcomingFrom && (
                      <div
                        className="mt-1.5 ml-8 inline-block px-1.5 py-0.5 border border-dotr-ink/40 font-mono text-[10px] uppercase tracking-widest text-dotr-inkmuted"
                        data-testid={`menu-upcoming-${slug}`}
                        title={`Price rises to ${fmtMoney(currencySymbol, upcoming)} on ${upcomingFrom}`}
                      >
                        → {fmtMoney(currencySymbol, upcoming)} from {upcomingFrom}
                      </div>
                    )}
                    {checked && (
                      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex flex-col">
                          <span className="label-uppercase">Quantity</span>
                          <span className="font-mono text-[11px] text-dotr-primary font-bold mt-0.5" data-testid={`menu-line-total-${slug}`}>
                            = {fmtMoney(currencySymbol, lineTotal)}
                          </span>
                        </div>
                        <QtyInput
                          value={itemsMap[name]}
                          onInc={() => incQty(name)}
                          onDec={() => decQty(name)}
                          onSet={(v) => setQty(name, v)}
                          testId={`menu-qty-${slug}`}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <aside className="lg:col-span-4">
        <div className="brutal-card p-5 md:p-6 sticky top-6" data-testid="cart-summary">
          <div className="flex items-center gap-2 mb-4 border-b-2 border-dotr-ink pb-3">
            <ShoppingBag size={18} />
            <h3 className="font-heading font-semibold text-xl uppercase tracking-tight">Order Summary</h3>
          </div>

          {Object.keys(itemsMap).length === 0 ? (
            <div className="font-mono text-xs text-dotr-inkmuted uppercase tracking-wider py-6 text-center">
              No items selected yet.
            </div>
          ) : (
            <ul className="space-y-2 mb-4" data-testid="cart-items">
              {Object.entries(itemsMap).map(([name, qty]) => {
                const line = (priceMap[name] || 0) * (parseInt(qty, 10) || 0);
                return (
                <li
                  key={name}
                  className="flex items-center justify-between gap-2 border-b border-dashed border-dotr-ink/30 pb-2"
                  data-testid={`cart-row-${slugify(name)}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-body text-sm truncate" title={name}>{name}</div>
                    <div className="font-mono text-[10px] text-dotr-inkmuted">
                      ×{qty} @ {fmtMoney(currencySymbol, priceMap[name] || 0)}
                    </div>
                  </div>
                  <span className="font-mono text-sm font-bold text-dotr-primary whitespace-nowrap" data-testid={`cart-line-total-${slugify(name)}`}>
                    {fmtMoney(currencySymbol, line)}
                  </span>
                  <button
                    type="button"
                    onClick={() => remove(name)}
                    className="text-dotr-inkmuted hover:text-dotr-primary"
                    aria-label={`Remove ${name}`}
                    data-testid={`cart-remove-${slugify(name)}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </li>
              );})}
            </ul>
          )}

          <div className="border-t-2 border-dotr-ink pt-3 flex items-center justify-between">
            <span className="label-uppercase">Total Items</span>
            <span className="font-heading font-semibold text-2xl text-dotr-ink tracking-tighter" data-testid="cart-total-qty">
              {totalQty}
            </span>
          </div>

          <div className="mt-2 pt-3 border-t border-dashed border-dotr-ink flex items-baseline justify-between">
            <span className="label-uppercase">Grand Total</span>
            <span className="font-heading font-semibold text-4xl text-dotr-primary tracking-tighter" data-testid="cart-grand-total">
              {fmtMoney(currencySymbol, grandTotal)}
            </span>
          </div>

          <div className="mt-5 pt-4 border-t border-dashed border-dotr-ink">
            <p className="font-mono text-[11px] uppercase tracking-widest text-dotr-inkmuted leading-relaxed flex items-center gap-1">
              Tick items, set qty <ChevronRight size={12} /> hit confirm.
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
};
